import { useEffect, useEffectEvent, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Skeleton } from 'antd'
import dayjs from 'dayjs'
import type { OrderContractSettings, RentalOrder } from '@super-mega/server/contracts/order'
import type { ContractLexicalContent } from '@/features/contract/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { OrderContractEditor } from './order-contract-editor.tsx'
import { RentalContractPrerequisites } from './rental-contract-prerequisites.tsx'

export type ContractOrder = {
  id: number
  orderNo: string
  customerName: string
  updatedAt: Date
  partyName: string
  settings: OrderContractSettings
  editable: boolean
  rentalOrder?: RentalOrder
  canSupplement?: boolean
}
export type OrderContractDraft = {
  key: string
  orderId: number
  contractType: OrderContractSettings[number]['contractType']
  templateId?: number
  templateVersionId?: number
  sourceDocumentId?: number
  partyName: string
  title: string
  content: ContractLexicalContent | null
  unresolved: string[]
  revision: number
  dirty: boolean
  readOnly?: boolean
  error?: string
}

export function OrderContractWorkspace({
  ids,
  documentId,
  orderType = 'rental',
  canEdit = true,
  canManage = canEdit,
  batch = false
}: {
  ids: number[]
  batch?: boolean
  documentId?: number
  orderType?: 'rental' | 'service'
  canEdit?: boolean
  canManage?: boolean
}) {
  const [skippedOrderIds, setSkippedOrderIds] = useState<Set<number>>(() => new Set())
  const trpc = useTRPC()
  const access = useAccess()
  const canWriteContracts = access.all(['contract.document.create', 'contract.document.confirm'])
  const canSupplement = canManage && access.can('order.rental.material.update')
  const rentals = useQueries({
    queries: ids.map((rentalOrderId) => ({
      ...trpc.order.rental.detail.queryOptions({ rentalOrderId }),
      enabled: orderType === 'rental'
    }))
  })
  const services = useQueries({
    queries: ids.map((serviceOrderId) => ({
      ...trpc.order.service.detail.queryOptions({ serviceOrderId }),
      enabled: orderType === 'service'
    }))
  })
  const documents = useQueries({
    queries: ids.map((orderId) => trpc.order.contracts.documents.queryOptions({ orderType, orderId, documentId }))
  })
  const templates = useQuery({ ...trpc.order.contracts.options.queryOptions(), enabled: canWriteContracts })
  const orders = orderType === 'rental' ? rentals : services
  const queries = [...orders, ...documents]
  const error = queries.find((query) => query.isError)?.error
  if (error) {
    return (
      <Alert
        type="error"
        title="合同加载失败"
        description={error.message}
        action={<Button onClick={() => queries.forEach((query) => void query.refetch())}>重试</Button>}
      />
    )
  }
  if (queries.some((query) => query.isPending) || (canWriteContracts && templates.isPending)) {
    return <Skeleton active className="p-6" />
  }
  if (documentId && !documents.some((query) => query.data?.some((document) => document.id === documentId))) {
    return <Alert className="m-6" showIcon type="warning" title="此合同不存在或不属于当前订单" />
  }
  const missing =
    canWriteContracts && orderType === 'rental' && !batch
      ? rentals.find(
          (query) =>
            query.data?.missingContractData.length &&
            !skippedOrderIds.has(query.data.id) &&
            !documents[ids.indexOf(query.data.id)]?.data?.length
        )?.data
      : undefined
  if (missing) {
    return (
      <RentalContractPrerequisites
        onSkip={() => setSkippedOrderIds((current) => new Set(current).add(missing.id))}
        rentalOrderId={missing.id}
        orderNo={missing.orderNo}
        missingData={missing.missingContractData}
        subjectId={missing.subjectId}
        unassociatedOrder={missing.canAssociateEnterprise && !missing.subjectId ? missing : undefined}
        canSupplement={canSupplement && missing.status === 'pending' && !missing.archivedAt}
      />
    )
  }
  const preparedOrders: ContractOrder[] = orders.map((query) => {
    const order = query.data!
    const legacy =
      'contractType' in order
        ? [
            {
              contractType: order.contractType ?? ('lease' as const),
              templateId: order.contractTemplateId ?? undefined
            }
          ]
        : []
    return {
      rentalOrder: 'canAssociateEnterprise' in order ? order : undefined,
      canSupplement,
      id: order.id,
      orderNo: order.orderNo,
      customerName: order.customerName,
      updatedAt: order.updatedAt,
      partyName:
        ('contractPartyName' in order ? order.contractPartyName : null) ||
        order.companyName ||
        order.identityName ||
        order.customerName,
      settings: order.contractSettings ?? legacy,
      editable:
        canWriteContracts &&
        (orderType === 'rental' && order.status !== 'pending' ? canManage : canEdit) &&
        !order.archivedAt &&
        !['ended', 'cancelled', 'archived'].includes(order.status)
    }
  })
  const initialDrafts = preparedOrders.flatMap<OrderContractDraft>((order) => {
    const saved = documents[ids.indexOf(order.id)].data ?? []
    const savedDrafts: OrderContractDraft[] = saved.map((document) => ({
      key: `saved-${document.id}`,
      orderId: order.id,
      contractType: document.type,
      templateId: document.templateId ?? undefined,
      templateVersionId: document.templateVersionId ?? undefined,
      sourceDocumentId: document.id,
      readOnly: !canWriteContracts || document.status === 'voided',
      partyName:
        document.sourceSnapshot &&
        typeof document.sourceSnapshot === 'object' &&
        'partyName' in document.sourceSnapshot &&
        typeof document.sourceSnapshot.partyName === 'string'
          ? document.sourceSnapshot.partyName
          : order.partyName,
      title: document.title,
      content: document.content as ContractLexicalContent,
      unresolved: [],
      revision: 0,
      dirty: false
    }))
    if (!order.editable) {
      return savedDrafts
    }
    const matched = new Set<number>()
    const settings = order.settings.filter((setting) => {
      const document = saved.find(
        (item) =>
          !matched.has(item.id) &&
          item.type === setting.contractType &&
          (!setting.templateId || item.templateId === setting.templateId)
      )
      if (!document) {
        return true
      }
      matched.add(document.id)
      return false
    })
    let draftSettings = settings
    if (!saved.length) {
      if (order.settings.length) {
        draftSettings = order.settings
      } else if (orderType === 'rental') {
        draftSettings = [{ contractType: 'lease' }]
      } else {
        draftSettings = [{ contractType: 'service' }]
      }
    }
    const newDrafts: OrderContractDraft[] = draftSettings.map((setting, index) => ({
      key: `${order.id}-${index}`,
      orderId: order.id,
      contractType: setting.contractType,
      templateId: setting.templateId,
      partyName: order.partyName,
      title: `${order.orderNo} · 合同 ${index + 1}`,
      content: null,
      unresolved: [],
      revision: 0,
      dirty: true
    }))
    return [...savedDrafts, ...newDrafts]
  })
  return (
    <OrderContractSession
      key={`${orderType}:${ids.join(':')}:${documentId ?? ''}`}
      documentId={documentId}
      orders={preparedOrders}
      initialDrafts={initialDrafts}
      templates={templates.data ?? []}
      orderType={orderType}
    />
  )
}

function OrderContractSession({
  orders,
  initialDrafts,
  templates,
  orderType,
  documentId
}: {
  orders: ContractOrder[]
  initialDrafts: OrderContractDraft[]
  templates: { id: number; name: string; type: string }[]
  orderType: 'rental' | 'service'
  documentId?: number
}) {
  const [drafts, setDrafts] = useState(initialDrafts)
  const [initializing, setInitializing] = useState(true)
  const trpc = useTRPC()
  const cache = useQueryClient()
  const initialize = useEffectEvent(async () =>
    Promise.all(
      initialDrafts.map(async (draft) => {
        if (
          draft.sourceDocumentId ||
          !draft.templateId ||
          !orders.find((order) => order.id === draft.orderId)?.editable
        ) {
          return draft
        }
        if (!templates.some((template) => template.id === draft.templateId)) {
          return { ...draft, templateId: undefined }
        }
        try {
          const prepared = await cache.fetchQuery(
            trpc.order.contracts.prepare.queryOptions({
              orderType,
              orderId: draft.orderId,
              templateId: draft.templateId,
              partyName: draft.partyName
            })
          )
          if (
            !dayjs(prepared.expectedUpdatedAt).isSame(orders.find((order) => order.id === draft.orderId)!.updatedAt)
          ) {
            throw new Error('订单已更新，请刷新页面后重新生成合同')
          }
          return { ...draft, ...prepared }
        } catch (error) {
          return { ...draft, error: error instanceof Error ? error.message : '模板加载失败' }
        }
      })
    )
  )
  useEffect(() => {
    let active = true
    void initialize().then((result) => {
      if (active) {
        setDrafts(result)
        setInitializing(false)
      }
    })
    return () => {
      active = false
    }
  }, [])
  if (initializing) {
    return <Skeleton active className="p-6" />
  }
  return (
    <OrderContractEditor
      orders={orders}
      drafts={drafts}
      setDrafts={setDrafts}
      templates={templates}
      orderType={orderType}
      documentId={documentId}
    />
  )
}
